export type MessengerEditCompletedAddress = '' | 'last' | 'route'

interface EditSettings {
    messengerEditCompletedAddress?: MessengerEditCompletedAddress
    /** Minutes after completing a stop during which it can be changed, no limit when empty */
    messengerEditCompletedAddressMinutes?: number | null
}

interface EditableRoute {
    started?: boolean
    completed?: boolean
    completedAddresses?: CompletedAddress[]
}

interface CompletedAddress {
    orderId?: string
    index?: number
    completedByReseller?: boolean
    completedAt?: string
}

/**
 * Whether the messenger can still change the outcome of a stop they completed (#701). Only stops on the active route
 * qualify, and depending on the reseller setting only the stop completed last or every completed stop of the route.
 * A stop completed by a planner is the planner's, not the messenger's, to correct. The reseller can limit how long
 * after completing a stop it can be changed, counted from the first completion so corrections don't extend it.
 */
export function canEditCompletedAddress(settings: EditSettings | undefined, route: EditableRoute | undefined, address: CompletedAddress): boolean {
    const setting = settings?.messengerEditCompletedAddress

    if (!setting || !route?.started || route.completed) {
        return false
    }

    const completedAddresses = route.completedAddresses || []
    const isSameStop = (completedAddress: CompletedAddress) => completedAddress.orderId === address.orderId && completedAddress.index === address.index

    const completedAddress = [...completedAddresses].reverse().find(isSameStop)

    if (!completedAddress || completedAddress.completedByReseller) {
        return false
    }

    const maxMinutes = settings.messengerEditCompletedAddressMinutes
    const completedAt = address.completedAt || completedAddress.completedAt

    if (maxMinutes && completedAt && Date.now() - Date.parse(completedAt) > maxMinutes * 60 * 1000) {
        return false
    }

    if (setting === 'last') {
        return isSameStop(completedAddresses[completedAddresses.length - 1])
    }

    return true
}
